import { useContext, useEffect, useMemo, useState } from "react";
import { API } from "../../../api";
import { TeamMember } from "../../../models";
import { UserContext } from "../../../hooks/UserContext";
import {
	generateTuningPassword,
	generateEngineeringPassword,
	generateWeeklyTechPassword,
	getDailyValidPeriod,
	getWeeklyValidPeriod,
} from "./MissionControlPasswordsGenerator.helpers";
import type { PasswordResult } from "./MissionControlPasswordsGenerator.types";

export const useMissionControlPasswordsGeneratorViewModel = () => {
	const currentUser = useContext(UserContext);
	const isSuperUser = currentUser?.isSuperUser ?? false;

	const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
	const [selectedUser, setSelectedUser] = useState<TeamMember | null>(null);
	const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (isSuperUser) {
			fetchTeamMembers();
		}
	}, [isSuperUser]);

	const fetchTeamMembers = async () => {
		setLoadingTeamMembers(true);
		setError(null);
		try {
			const members = await API.Data.TeamMembers.get.byFilter({
				fields: {
					appUserId: { operator: "is_not_empty" },
				},
			});
			members.sort((a, b) => a.name.localeCompare(b.name));
			setTeamMembers(members);
		} catch (err: any) {
			setError(`Failed to load team members: ${err.message || "Unknown error"}`);
		} finally {
			setLoadingTeamMembers(false);
		}
	};

	const today = useMemo(() => new Date(), []);

	const passwords: PasswordResult[] = useMemo(() => {
		if (!selectedUser) return [];

		const validPeriod = getDailyValidPeriod(today);
		return [
			{
				label: "Ops (Tuning)",
				password: generateTuningPassword(selectedUser.appUserId, today),
				validPeriod,
			},
			{
				label: "Technician (Engineering)",
				password: generateEngineeringPassword(selectedUser.appUserId, today),
				validPeriod,
			},
		];
	}, [selectedUser, today]);

	const weeklyPassword: PasswordResult | null = useMemo(() => {
		return {
			label: "Weekly Technician",
			password: generateWeeklyTechPassword(today),
			validPeriod: getWeeklyValidPeriod(today),
		};
	}, [today]);

	return {
		isSuperUser,
		teamMembers,
		selectedUser,
		setSelectedUser,
		loadingTeamMembers,
		error,
		passwords,
		weeklyPassword,
	};
};
